import { addMinutes, format, isValid, parseISO } from 'date-fns';
import * as _ from 'lodash';

import { DisposalSiteModel } from '@digital-platform/disposal-sites/domain';
import { GeneratorModel } from '@digital-platform/generators/domain';
import { Signature } from '@digital-platform/sign-now/domain';
import { UserModel } from '@digital-platform/users/domain';
import {
  InternalStatusHistory,
  CommentLogHistory,
  WasteProfileAdminCard,
  WasteProfileSearch,
  WasteProfile,
  GetAllWasteProfile,
} from '../interfaces';
import { WasteProfileCustomerCard } from '../interfaces/waste-profile-customer-card.interface';
import {
  InternalStatusHistoryModel,
  SpecialWasteProfileModel,
  ExpressWasteProfileModel,
  DeepwellWasteProfileModel,
  WasteProfileModel,
  WasteProfileSearchModel,
} from '../models';
import { GetAllWasteProfilesModel } from '../models/get-all-waste-profiles-model';
import { PhysicalComponentModel } from '../models/physical-component.model';
import { WasteProfileAdminCardModel } from '../models/waste-profile-admin-card.model';
import { WasteProfileCustomerCardModel } from '../models/waste-profile-customer-card-model';
import {
  DeepwellWasteProfileJSON,
  LabAnalyticalAttachmentsJSON,
  SpecialWasteProfileJSON,
  WasteProfileCardJSON,
  WasteProfileCustomerCardJSON,
  WasteProfileJSON,
  WasteProfileJSONTypes,
  WasteProfileTypes,
} from '../types';

// TODO: Move all of this logic to the WasteProfileModel
export function formatDate(parsedTime) {
  if (!parsedTime) {
    return null;
  }
  return format(
    addMinutes(parsedTime, parsedTime.getTimezoneOffset()),
    'yyyy-MM-dd HH:mm:ss'
  );
}

// TODO: Move all of this logic to the WasteProfileModel
export function parseWasteProfile(
  wasteProfile: WasteProfileJSONTypes
): WasteProfileTypes {
  if (wasteProfile['coordinatorName'] !== undefined) {
    // pulls the field out of the profile object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { coordinatorName, ...data } = wasteProfile as any;

    // map if we have no coordinator object and a non-empty name string
    let coordinator = null;
    if (!wasteProfile.coordinator && coordinatorName !== '') {
      coordinator = {
        ...UserModel.DEFAULT_CONFIG,
        name: coordinatorName,
      };
    }

    // put the coordinator object in and remove the name field
    wasteProfile = {
      ...data,
      coordinator,
    } as WasteProfileJSONTypes;
  }

  // transfer landfill name to destination name.
  if (!wasteProfile.destination && wasteProfile.landfill) {
    wasteProfile = {
      ...wasteProfile,
      destination: {
        ...DisposalSiteModel.DEFAULT_CONFIG,
        name: wasteProfile.landfill,
      },
    };
  }

  // update color option if clear is selected
  if (wasteProfile?.physicalComponent?.dominantColor === 'Clear') {
    wasteProfile = {
      ...wasteProfile,
      physicalComponent: {
        ...PhysicalComponentModel.DEFAULT_CONFIG,
        ...wasteProfile.physicalComponent,
        dominantColor: 'Colorless',
      },
    };
  }

  if (wasteProfile.physicalComponent?.otherColor?.includes('Clear')) {
    wasteProfile = {
      ...wasteProfile,
      physicalComponent: {
        ...PhysicalComponentModel.DEFAULT_CONFIG,
        ...wasteProfile.physicalComponent,
      },
    };

    const index = wasteProfile.physicalComponent?.otherColor?.findIndex(
      (c) => c === 'Clear'
    );
    wasteProfile.physicalComponent.otherColor[index] = 'Colorless';
  }

  // populate the yearCode and logbookSequenceNumber from the waste profile number if necessary
  const { number } = wasteProfile;
  let { logbookSequenceNumber, yearCode } = wasteProfile;
  if ((!logbookSequenceNumber || !yearCode) && number) {
    logbookSequenceNumber = logbookSequenceNumber || number.substring(6);
    yearCode = yearCode || number.substring(4, 6);
    if (logbookSequenceNumber.length < 4) {
      logbookSequenceNumber = logbookSequenceNumber.padStart(4, '0');
    }
  }
  wasteProfile = {
    ...wasteProfile,
    logbookSequenceNumber,
    yearCode,
  };

  // ensure meta section is consistent
  const {
    meta: { sections },
  } = wasteProfile;
  for (const section of Object.keys(sections)) {
    sections[section] = {
      completed: sections[section].completed || false,
      valid: sections[section].valid || false,
    };
  }
  wasteProfile = {
    ...wasteProfile,
    meta: { ...wasteProfile.meta, sections },
  };

  // hydrate log entries
  const log: WasteProfileTypes['log'] = wasteProfile.log || [];
  wasteProfile = {
    ...wasteProfile,
    log: log.map(InternalStatusHistoryModel.hydrate),
  };

  // filter log entries to only those with status changes
  wasteProfile = {
    ...wasteProfile,
    log: wasteProfile.log.reduce<InternalStatusHistory[]>(
      (filteredLog, entry) => {
        if (filteredLog.length === 0) {
          filteredLog.push(entry);
          return filteredLog;
        }

        const previousEntry = filteredLog[filteredLog.length - 1];
        if (
          entry.status !== previousEntry.status ||
          entry.internalStatus !== previousEntry.internalStatus
        ) {
          filteredLog.push(entry);
        }

        return filteredLog;
      },
      []
    ),
  };

  switch (wasteProfile.type) {
    case undefined:
    case null:
    case 'special-waste':
      return new SpecialWasteProfileModel({
        ...SpecialWasteProfileModel.DEFAULT_CONFIG, // backfills potentially missing schema elements
        ...parseDates(wasteProfile as SpecialWasteProfileJSON),
        meta: {
          ...SpecialWasteProfileModel.DEFAULT_CONFIG.meta,
          ...wasteProfile.meta,
          sections: {
            ...SpecialWasteProfileModel.DEFAULT_CONFIG.meta.sections,
            ...wasteProfile.meta.sections,
          },
        },
        generator: wasteProfile.generator
          ? GeneratorModel.hydrate(wasteProfile.generator)
          : null,
      });
    case 'express':
      return new ExpressWasteProfileModel({
        ...ExpressWasteProfileModel.DEFAULT_CONFIG, // backfills potentially missing schema elements
        ...parseDates(wasteProfile as WasteProfileJSON),
        meta: {
          ...ExpressWasteProfileModel.DEFAULT_CONFIG.meta,
          ...wasteProfile.meta,
          sections: {
            ...ExpressWasteProfileModel.DEFAULT_CONFIG.meta.sections,
            ...wasteProfile.meta.sections,
          },
        },
        generator: wasteProfile.generator
          ? GeneratorModel.hydrate(wasteProfile.generator)
          : null,
      });
    case 'deepwell':
      return new DeepwellWasteProfileModel({
        ...DeepwellWasteProfileModel.DEFAULT_CONFIG, // backfills potentially missing schema elements
        ...parseDates(wasteProfile as DeepwellWasteProfileJSON),
        meta: {
          ...DeepwellWasteProfileModel.DEFAULT_CONFIG.meta,
          ...wasteProfile.meta,
          sections: {
            ...DeepwellWasteProfileModel.DEFAULT_CONFIG.meta.sections,
            ...wasteProfile.meta.sections,
          },
        },
        generator: wasteProfile.generator
          ? GeneratorModel.hydrate(wasteProfile.generator)
          : null,
      });
    default:
      return new WasteProfileModel({
        ...WasteProfileModel.DEFAULT_CONFIG, // backfills potentially missing schema elements
        ...parseDates(wasteProfile as WasteProfileJSON),
        meta: {
          ...WasteProfileModel.DEFAULT_CONFIG.meta,
          ...wasteProfile.meta,
          sections: {
            ...WasteProfileModel.DEFAULT_CONFIG.meta.sections,
            ...wasteProfile.meta.sections,
          },
        },
        generator: wasteProfile.generator
          ? GeneratorModel.hydrate(wasteProfile.generator)
          : null,
      });
  }
}

// function isWasteProfileJSON(
//   wasteProfile: WasteProfileJSONTypes
// ): wasteProfile is WasteProfileJSONTypes {
//   return wasteProfile.schemaVersion === 1 || !!wasteProfile.meta;
// }

// function isWasteProfileCardJSON(
//   wasteProfile: WasteProfileCardJSON
// ): wasteProfile is WasteProfileCardJSON {
//   return wasteProfile.schemaVersion === 1 || !!wasteProfile.meta;
// }

// function isWasteProfileCustomerCardJSON(
//   wasteProfile: WasteProfileCustomerCardJSON
// ): wasteProfile is WasteProfileCustomerCardJSON {
//   return wasteProfile.schemaVersion === 1 || !!wasteProfile.meta;
// }

// TODO: Move all of this logic to the WasteProfileModel

// TODO: Move all of this logic to the WasteProfileModel
function parseDates<WasteProfileCardJSON, WasteProfileAdminCard>(
  wasteProfile: WasteProfileCardJSON
): WasteProfileAdminCard;
function parseDates<WasteProfileCustomerCardJSON, WasteProfileCustomerCard>(
  wasteProfile: WasteProfileCustomerCardJSON
): WasteProfileCustomerCard;
function parseDates<WasteProfileJSON, WasteProfile>(
  wasteProfile: WasteProfileJSON
): WasteProfile;
function parseDates<SpecialWasteProfileJSON, SpecialWasteProfile>(
  wasteProfile: SpecialWasteProfileJSON
): SpecialWasteProfile;
function parseDates<ExpressWasteProfileJSON, ExpressWasteProfile>(
  wasteProfile: ExpressWasteProfileJSON
): ExpressWasteProfile;
function parseDates<DeepwellWasteProfileJSON, DeepwellWasteProfile>(
  wasteProfile: DeepwellWasteProfileJSON
): DeepwellWasteProfile;
function parseDates<
  T extends WasteProfileJSONTypes,
  R extends WasteProfileTypes
>(wasteProfile: T): R {
  const parse = (value) => {
    if (!value) {
      return null;
    }

    const parsed = parseISO(value);

    return isValid(parsed) ? parsed : null;
  };

  const parseLabAnalyticalAttachments = (
    labAnalyticalAttachments: LabAnalyticalAttachmentsJSON
  ) => {
    if (!labAnalyticalAttachments) {
      return [];
    }
    return labAnalyticalAttachments.map((labAnalyticalAttachment) => {
      if (!labAnalyticalAttachment) {
        return null;
      }

      return {
        ...labAnalyticalAttachment,
        samples: labAnalyticalAttachment.samples.map((sample) => ({
          ...sample,
          sampleDate: parse(sample.sampleDate),
        })),
      };
    });
  };

  const parseSignatures = (signatures: Signature[]): Signature[] => {
    if (!signatures) {
      return [];
    }
    return signatures.map((signature) => {
      if (!signature) {
        return null;
      }

      return {
        ...signature,
        date: formatDate(parse(signature?.date)),
      };
    });
  };

  let pendingRecertification = null;
  if (wasteProfile.pendingRecertification) {
    pendingRecertification = {
      ...wasteProfile.pendingRecertification,
      signedDate: parse(wasteProfile.pendingRecertification.signedDate),
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        wasteProfile.pendingRecertification.labAnalyticalAttachments
      ),
    };
  }

  let pendingChangeRequest = null;
  if (wasteProfile.pendingChangeRequest) {
    pendingChangeRequest = {
      ...wasteProfile.pendingChangeRequest,
      expiryDate: parse(wasteProfile.pendingChangeRequest.expiryDate),
      signedDate: parse(wasteProfile.pendingChangeRequest.signedDate),
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        wasteProfile.pendingChangeRequest.labAnalyticalAttachments
      ),
    };
  }

  let parsed = {
    ...wasteProfile,
    createdDate: parse(wasteProfile.createdDate),
    lastModifiedDate: parse(wasteProfile.lastModifiedDate),
    submittedDate: parse(wasteProfile.submittedDate),
    certifiedDate: parse(wasteProfile.certifiedDate),
    expectedResponseDate: parse(wasteProfile.expectedResponseDate),
    approvedDate: parse(wasteProfile.approvedDate),
    rejectedDate: parse(wasteProfile.rejectedDate),
    incompleteDate: parse(wasteProfile.incompleteDate),
    expiryDate: parse(wasteProfile.expiryDate),
    pendingRecertification,
    pendingChangeRequest,
    signatures: parseSignatures(wasteProfile.signatures),
  } as unknown as R; // This feels shaky

  if (
    SpecialWasteProfileModel.isSpecialWaste(parsed) ||
    DeepwellWasteProfileModel.isDeepWell(parsed)
  ) {
    parsed = {
      ...parsed,
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        // this is a gross workaround to make typescript happy, we need to revisit the
        // typing and parsing logic for waste profiles or else it will become even more
        // spaghetti like this
        // Refactoring the JSON types like this makes it less so, but it still feels weird
        (wasteProfile as SpecialWasteProfileJSON | DeepwellWasteProfileJSON)
          .labAnalyticalAttachments
      ),
    };
  }

  return parsed;
}

export function removeSlashes(wasteProfile: WasteProfile): WasteProfile {
  const updatedObject = {
    ...wasteProfile,
    // Remove any extra slashes from the processGeneratingWaste field
    processGeneratingWaste: wasteProfile.processGeneratingWaste.replace(
      /\/\(/g,
      '('
    ),
  };
  return updatedObject;
}

// TODO: Move all of this logic to the WasteProfileModel
export function stringifyDates<
  T extends WasteProfileTypes,
  R extends WasteProfileJSONTypes
>(wasteProfile: T): R {
  const parse = (value) => {
    if (!value) {
      return null;
    }

    if (!(value instanceof Date)) {
      return value;
    }

    const parsed = value.toISOString();

    return parsed ? parsed : null;
  };

  // TODO: Move all of this logic to the WasteProfileModel
  // TODO: Move Waste Profile Logs to their own document type
  const parseLogs = (logs: InternalStatusHistory[] | CommentLogHistory[]) =>
    logs.map((log: InternalStatusHistory | CommentLogHistory) => ({
      ...log,
      date: parse(log.date),
    }));

  // TODO: Move all of this logic to the WasteProfileModel
  const parseLabAnalyticalAttachments = (
    labAnalyticalAttachments
  ): LabAnalyticalAttachmentsJSON => {
    if (!labAnalyticalAttachments) {
      return [];
    }
    return labAnalyticalAttachments.map((labAnalyticalAttachment) => {
      if (!labAnalyticalAttachment) {
        return null;
      }

      return {
        ...labAnalyticalAttachment,
        attachment: {
          ...labAnalyticalAttachment.attachment,
          uploadDate: parse(labAnalyticalAttachment.attachment.uploadDate),
        },
        samples: labAnalyticalAttachment.samples.map((sample) => ({
          ...sample,
          sampleDate: parse(sample.sampleDate),
        })),
      };
    });
  };

  const parseUploadDate = (attachmentContainers) => {
    if (!attachmentContainers) {
      return [];
    }
    return attachmentContainers.map((attachmentContainer) => {
      if (!attachmentContainer) {
        return null;
      }

      if (attachmentContainer.attachment) {
        return {
          ...attachmentContainer,
          attachment: {
            ...attachmentContainer.attachment,
            uploadDate: parse(attachmentContainer.attachment.uploadDate),
          },
        };
      } else {
        return {
          ...attachmentContainer,
          uploadDate: parse(attachmentContainer.uploadDate),
        };
      }
    });
  };

  // TODO: Move all of this logic to the WasteProfileModel
  const parseSignatures = (signatures: Signature[]) => {
    if (!signatures) {
      return [];
    }
    return signatures.map((signature) => {
      if (!signature) {
        return null;
      }

      return {
        ...signature,
        date: parse(signature.date),
      };
    });
  };

  let pendingRecertification = null;
  if (wasteProfile.pendingRecertification) {
    pendingRecertification = {
      ...wasteProfile.pendingRecertification,
      signedDate: parse(wasteProfile.pendingRecertification.signedDate),
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        wasteProfile.pendingRecertification.labAnalyticalAttachments
      ),
      safetyDataSheets: parseUploadDate(
        wasteProfile.pendingRecertification?.safetyDataSheets
      ),
      additionalSupplements: parseUploadDate(
        wasteProfile.pendingRecertification?.additionalSupplements
      ),
      stateAttachments: parseUploadDate(
        wasteProfile.pendingRecertification?.stateAttachments
      ),
    };
  }

  const recertificationArchive = [];
  if (wasteProfile.recertificationArchive) {
    for (const recertification of wasteProfile.recertificationArchive) {
      console.log(
        'zzz recertification.archiveDate',
        recertification.archiveDate
      );
      recertificationArchive.push({
        ...recertification,
        signedDate: parse(recertification.signedDate),
        labAnalyticalAttachments: parseLabAnalyticalAttachments(
          recertification.labAnalyticalAttachments
        ),
        safetyDataSheets: parseUploadDate(recertification?.safetyDataSheets),
        additionalSupplements: parseUploadDate(
          recertification?.additionalSupplements
        ),
        stateAttachments: parseUploadDate(recertification?.stateAttachments),
        archiveDate: parse(recertification.archiveDate),
      });
    }
  }

  const changeRequestArchive = [];
  if (wasteProfile.changeRequestArchive) {
    for (const change of wasteProfile.changeRequestArchive) {
      changeRequestArchive.push({
        ...change,
        expiryDate: parse(change.expiryDate),
        signedDate: parse(change.signedDate),
        labAnalyticalAttachments: parseLabAnalyticalAttachments(
          change.labAnalyticalAttachments
        ),
        safetyDataSheets: parseUploadDate(change?.safetyDataSheets),
        additionalSupplements: parseUploadDate(change?.additionalSupplements),
        stateAttachments: parseUploadDate(change?.stateAttachments),
        archiveDate: parse(change.archiveDate),
      });
    }
  }
  let pendingChangeRequest = null;
  if (wasteProfile.pendingChangeRequest) {
    pendingChangeRequest = {
      ...wasteProfile.pendingChangeRequest,
      expiryDate: parse(wasteProfile.pendingChangeRequest.expiryDate),
      signedDate: parse(wasteProfile.pendingChangeRequest.signedDate),
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        wasteProfile.pendingChangeRequest.labAnalyticalAttachments
      ),
      safetyDataSheets: parseUploadDate(
        wasteProfile.pendingChangeRequest?.safetyDataSheets
      ),
      additionalSupplements: parseUploadDate(
        wasteProfile.pendingChangeRequest?.additionalSupplements
      ),
      stateAttachments: parseUploadDate(
        wasteProfile.pendingChangeRequest?.stateAttachments
      ),
    };
  }
  let parsed: R = {
    ...wasteProfile,
    createdDate: parse(wasteProfile.createdDate),
    lastModifiedDate: parse(wasteProfile.lastModifiedDate),
    submittedDate: parse(wasteProfile.submittedDate),
    certifiedDate: parse(wasteProfile.certifiedDate),
    expectedResponseDate: parse(wasteProfile.expectedResponseDate),
    approvedDate: parse(wasteProfile.approvedDate),
    rejectedDate: parse(wasteProfile.rejectedDate),
    incompleteDate: parse(wasteProfile.incompleteDate),
    expiryDate: parse(wasteProfile.expiryDate),
    pendingRecertification,
    recertificationArchive,
    pendingChangeRequest,
    changeRequestArchive,
    signatures: parseSignatures(wasteProfile.signatures),
    log: parseLogs(wasteProfile.log),
    commentLog: parseLogs(wasteProfile.commentLog),
    thirdPartyAuthAttachment: wasteProfile.thirdPartyAuthAttachment
      ? {
          ...wasteProfile.thirdPartyAuthAttachment,
          uploadDate: parse(wasteProfile.thirdPartyAuthAttachment?.uploadDate),
        }
      : null,
  } as unknown as R;

  if (SpecialWasteProfileModel.isSpecialWaste(wasteProfile)) {
    parsed = {
      ...parsed,
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        wasteProfile?.labAnalyticalAttachments
      ),
      safetyDataSheets: parseUploadDate(wasteProfile?.safetyDataSheets),
      additionalSupplements: parseUploadDate(
        wasteProfile?.additionalSupplements
      ),
      stateAttachments: parseUploadDate(wasteProfile?.stateAttachments),
    };
  }

  if (DeepwellWasteProfileModel.isDeepWell(wasteProfile)) {
    parsed = {
      ...parsed,
      labAnalyticalAttachments: parseLabAnalyticalAttachments(
        wasteProfile.labAnalyticalAttachments
      ),
      safetyDataSheets: parseUploadDate(wasteProfile?.safetyDataSheets),
      additionalSupplements: parseUploadDate(
        wasteProfile?.additionalSupplements
      ),
    };
  }

  if (ExpressWasteProfileModel.isExpressWaste(wasteProfile)) {
    parsed = {
      ...parsed,
      additionalSupplements: parseUploadDate(
        wasteProfile?.additionalSupplements
      ),
      stateAttachments: parseUploadDate(wasteProfile.stateAttachments),
    };
  }

  return parsed;
}

// TODO: Move all of this logic to the WasteProfileModel
export function parseWasteProfileCard(
  wasteProfile: WasteProfileCardJSON
): WasteProfileAdminCard {
  if (wasteProfile['coordinatorName'] !== undefined) {
    // pulls the field out of the profile object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { coordinatorName, ...data } = wasteProfile as any;

    // map if we have no coordinator object and a non-empty name string
    let coordinator = null;
    if (!wasteProfile.coordinator && coordinatorName !== '') {
      coordinator = {
        ...UserModel.DEFAULT_CONFIG,
        name: coordinatorName,
      };
    }

    // put the coordinator object in and remove the name field
    wasteProfile = {
      ...data,
      coordinator,
    } as WasteProfileCardJSON;
  }
  return new WasteProfileAdminCardModel({
    ...WasteProfileAdminCardModel.DEFAULT_CONFIG,
    ...parseDates(wasteProfile as WasteProfileCardJSON),
  });
}
export function parseAllWasteProfiles(
  wasteProfile: GetAllWasteProfile
): GetAllWasteProfile {
  return new GetAllWasteProfilesModel({
    ...GetAllWasteProfilesModel.DEFAULT_CONFIG,
    ...parseDates(wasteProfile as GetAllWasteProfile),
  });
}

// TODO: Move all of this logic to the WasteProfileModel
export function parseWasteProfileCustomerCard(
  wasteProfile: WasteProfileCustomerCardJSON
): WasteProfileCustomerCard {
  return new WasteProfileCustomerCardModel({
    ...WasteProfileCustomerCardModel.DEFAULT_CONFIG,
    ...parseDates(wasteProfile as WasteProfileCustomerCardJSON),
  });
}

// TODO: Move all of this logic to the WasteProfileModel
export function parseWasteProfileSearch(
  wasteProfile: WasteProfileJSONTypes
): WasteProfileSearch {
  // populate the yearCode and logbookSequenceNumber from the waste profile number if necessary
  const { number } = wasteProfile;
  let { logbookSequenceNumber, yearCode } = wasteProfile;
  if ((!logbookSequenceNumber || !yearCode) && number) {
    logbookSequenceNumber = logbookSequenceNumber || number.substring(6);
    yearCode = yearCode || number.substring(4, 6);
    if (logbookSequenceNumber.length < 4) {
      logbookSequenceNumber = logbookSequenceNumber.padStart(4, '0');
    }
  }
  wasteProfile = {
    ...wasteProfile,
    logbookSequenceNumber,
    yearCode,
  };
  return new WasteProfileSearchModel({
    ...WasteProfileSearchModel.DEFAULT_CONFIG,
    ...parseDates(wasteProfile as WasteProfileJSONTypes),
  });
}

// Gets a new date object based on just the UTC date to avoid time zone changing the displayed date
export const dateOrNullWithoutTimezone = (date: string): Date | null => {
  const parsed = parseISO(date);

  if (parsed.toString() === 'Invalid Date') {
    return null;
  }

  const dateStringWithoutTimezone = new Date(
    `${parsed.getUTCFullYear()}-${
      parsed.getUTCMonth() + 1
    }-${parsed.getUTCDate()}`
  );
  return dateStringWithoutTimezone.toString() !== 'Invalid Date'
    ? dateStringWithoutTimezone
    : null;
};
